import React, { useState } from 'react'
import {
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  Box,
  Paper
} from '@mui/material'
import { AsnAdjustDetail } from '../../../interfaces/Inbound/InboundAdjust'
import { AsnAdjustWorkOrderLpn } from '../../../interfaces/AsnAdjust/AsnAdjust'
import { UnifiedGridRow } from './UnifiedGridRow'
import { dataGridSettings } from './GridElementModal/DataGridSettings'

interface UnifiedGridProps {
  items: (AsnAdjustDetail | AsnAdjustWorkOrderLpn)[]
  workOrderId: number
  hasLpns: boolean
  showName: boolean
  onDisplayChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onLineSaved: () => void
  externalOpenSnackBar?: (msg: string, status: boolean) => void
}

export const UnifiedGrid: React.FC<UnifiedGridProps> = ({
  items,
  workOrderId,
  hasLpns,
  showName,
  onDisplayChange,
  onLineSaved,
  externalOpenSnackBar
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const handleRowExpand = (index: number) => {
    const newExpandedRows = new Set(expandedRows)
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index)
    } else {
      newExpandedRows.add(index)
    }
    setExpandedRows(newExpandedRows)
  }

  const renderHeader = () => (
    <Paper
      elevation={0}
      style={{
        padding: '12px 16px',
        marginBottom: '2px',
        backgroundColor: '#f9f9f9',
        border: '1px solid #e0e0e0',
        borderRadius: '8px'
      }}
    >
      <Grid container alignItems='center' spacing={1}>
        {dataGridSettings.columns.map((col: any) => {
          let content: React.ReactNode

          if (col.dataField === 'nombre') {
            content = (
              <FormControlLabel
                control={
                  <Switch
                    checked={showName}
                    onChange={onDisplayChange}
                    name='checkedB'
                    color='primary'
                    size='small'
                  />
                }
                label={
                  <Typography style={{ fontSize: '12px', fontWeight: '600' }}>
                    {showName ? 'SKU/EAN' : 'NOMBRE'}
                  </Typography>
                }
              />
            )
          } else if (col.dataField === 'expand') {
            content = <Typography variant='subtitle2'></Typography>
          } else if (col.dataField === 'Actions') {
            content = <div />
          } else {
            content = (
              <Typography style={{ fontSize: '12px', fontWeight: '600', color: '#333' }}>
                {col.title}
              </Typography>
            )
          }

          return (
            <Grid
              item
              key={`header-${col.dataField}`}
              xs={col.xs}
              sm={col.sm}
              lg={col.lg}
              style={{ textAlign: 'center' }}
            >
              {content}
            </Grid>
          )
        })}
      </Grid>
    </Paper>
  )

  return (
    <Box style={{ padding: '12px' }}>
      {renderHeader()}

      {items.length === 0 ? (
        <Typography variant='body2' align='center' style={{ padding: '32px', color: '#999', fontSize: '13px' }}>
          No hay datos para mostrar
        </Typography>
      ) : (
        items.map((item, index) => {
          // Determinar la key según el tipo de item
          let key: string | number
          if (hasLpns && 'lpn' in item) {
            key = (item as AsnAdjustWorkOrderLpn).lpn || index
          } else {
            const adjustItem = item as AsnAdjustDetail
            key = adjustItem.skuId || adjustItem.sku || index
          }

          return (
            <UnifiedGridRow
              key={key}
              item={item}
              index={index}
              workOrderId={workOrderId}
              hasLpns={hasLpns}
              showName={showName}
              isExpanded={expandedRows.has(index)}
              onExpand={() => handleRowExpand(index)}
              onLineSaved={onLineSaved}
              externalOpenSnackBar={externalOpenSnackBar}
            />
          )
        })
      )}
    </Box>
  )
}
